import api from 'services/api';
import type { Recurrence } from 'api/recurrence_definitions/endpoints/types';
import { REJECT, array, object, str, type Received } from 'validation';
import { readResponse } from 'api/shared/readResponse';

export interface OccurrenceSeriesRequest {
  /** The first run datetime; the instance chain's immutable anchor. */
  readonly first_occurrence: string;
  readonly timezone?: string;
  /**
   * Every rule in effect, in ONE request. The engine takes the earliest
   * candidate across rules at each step, so a series merged from per-rule
   * responses would not match what the scheduler produces.
   */
  readonly recurrences?: readonly Recurrence[];
  readonly scheduling_window?: string;
  /** How many following occurrences to return (1..10). */
  readonly count?: number;
}

export interface OccurrenceSeriesResponse {
  /**
   * The occurrences AFTER first_occurrence. Shorter than the requested count
   * when the schedule runs out; empty when there is no recurrence.
   */
  readonly occurrences: ReadonlyArray<string>;
}

// Boundary validation guarantees the shape only: `occurrences` is present and
// an array of strings. Whether each string parses as a datetime is a value
// concern owned by the consumer, which feeds them to moment() and guards
// isValid() — the same split as fetchFirstOccurrence.
const occurrenceSeriesSchema = object<OccurrenceSeriesResponse>({
  occurrences: array(str(), REJECT),
});

/**
 * Previews the occurrences following a given first occurrence.
 *
 * The backend computes these with the same engine the scheduler runs for each
 * successor instance, so the preview cannot drift from what actually gets
 * scheduled. The first occurrence itself is not returned: it is the caller's
 * requested start, possibly moved to the next window opening at submit time.
 *
 * NOTE: a recurrence rule's `start_time` applies from the SECOND occurrence on,
 * so the gap between the first occurrence and the first entry here can be
 * shorter than the rule's interval.
 */
export async function fetchOccurrenceSeries(body: OccurrenceSeriesRequest): Promise<Received<OccurrenceSeriesResponse>> {
  const res = await api.post('/api/recurrence/occurrence-series', body);
  return readResponse(res.data, { schema: occurrenceSeriesSchema, source: 'fetchOccurrenceSeries' });
}
